import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck, Truck, CreditCard, Sparkles, Headset } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay = 0 }) => (
  <motion.span className="block"
    initial={{ opacity: 0, y: 44 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1.1, delay, ease: EASE }}>
    {children}
  </motion.span>
);

const HERO = "https://images.unsplash.com/photo-1748290880596-2a2c80530bc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const COL = {
  "Primavera": "https://images.unsplash.com/photo-1618085222100-93f0eecad0aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Verão": "https://images.unsplash.com/photo-1522218296581-1244648997fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Outono": "https://images.unsplash.com/photo-1772449100716-c12cb4e1f21a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "Inverno": "https://images.unsplash.com/photo-1697924293303-34488b60bf36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const MANIFESTO = [
  { n: "01", t: "Qualidade", d: "Peças selecionadas com tecidos nobres e acabamento impecável em cada detalhe.", icon: Sparkles },
  { n: "02", t: "Compra Segura", d: "Ambiente 100% seguro, com pagamento facilitado via Pix, cartão e boleto.", icon: ShieldCheck },
  { n: "03", t: "Pagamento Facilitado", d: "Parcele em até 12 vezes no cartão, conforme as condições da loja.", icon: CreditCard },
  { n: "04", t: "Entrega Brasil", d: "Enviamos para todo o Brasil com código de rastreamento para acompanhar.", icon: Truck },
  { n: "05", t: "Atendimento Dedicado", d: "Suporte antes e depois da compra, de segunda a sexta, das 8h às 18h.", icon: Headset },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.25, 0.7]);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const featured = products.filter((p) => p.tags?.includes("destaque")).slice(0, 4);
  const bestsellers = products.filter((p) => p.tags?.includes("mais_vendido")).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[92vh] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <img src={HERO} alt="Donna Nara" className="w-full h-[120%] object-cover object-top" />
          <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
        </motion.div>

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="label-caps text-rose mb-6">Coleção 2026 · Todas as Estações</motion.p>
          <h1 className="font-serif-display font-light text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[1.02] max-w-5xl">
            <Line delay={0.15}>Elegância, estilo</Line>
            <Line delay={0.28}>e tendências para</Line>
            <Line delay={0.41}><span className="font-bodoni italic text-rose">todas as mulheres.</span></Line>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/loja" data-testid="hero-shop-button"
              className="group bg-rose hover:bg-rose-hover text-ink font-bold px-8 py-4 label-caps flex items-center gap-3 transition-colors duration-300">
              Explorar Coleção <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link to="/loja?tag=promocao" data-testid="hero-promo-button"
              className="border border-white/25 hover:border-white text-white px-8 py-4 label-caps transition-colors duration-300">
              Ver Promoções
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 border-y border-line bg-ink">
        <Marquee speed={28} gradient={false}>
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="font-serif-display italic text-stroke text-6xl md:text-8xl mx-10">
              Moda que inspira. Elegância que acompanha você.
            </span>
          ))}
        </Marquee>
      </section>

      {/* COLLECTIONS BENTO */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-24" data-testid="collections-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-caps text-rose mb-3">Nossas Coleções</p>
            <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl tracking-tight leading-none">Quatro estações,<br /><span className="italic font-bodoni">infinitos estilos.</span></h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[220px] md:auto-rows-[280px]">
          {Object.entries(COL).map(([name, img], i) => (
            <Link key={name} to={`/loja?col=${name}`} data-testid={`collection-${name}`}
              className={`group relative overflow-hidden bg-surface2 ${i % 3 === 0 ? "row-span-2" : ""}`}>
              <img src={img} alt={name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="label-caps text-rose text-[10px] mb-1">Coleção</p>
                <h3 className="font-serif-display text-3xl text-white group-hover:text-rose transition-colors duration-300">{name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-12" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl tracking-tight">Destaques</h2>
          <Link to="/loja" className="label-caps text-white/60 hover:text-rose transition-colors duration-300 flex items-center gap-2">Ver tudo <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-28" data-testid="manifesto-section">
        <p className="label-caps text-rose mb-3">Por que comprar na Donna Nara</p>
        <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl tracking-tight leading-none mb-16 max-w-3xl">
          Vestir mulheres com <span className="italic font-bodoni text-rose">estilo e confiança.</span>
        </h2>
        <div className="space-y-2">
          {MANIFESTO.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div key={m.n}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group relative grid md:grid-cols-[160px_1fr_auto] items-center gap-6 py-8 border-t border-line hover:border-rose/40 transition-colors duration-500">
                <span className="font-bodoni italic text-6xl md:text-7xl text-white/10 group-hover:text-rose/30 transition-colors duration-500">{m.n}</span>
                <div>
                  <h3 className="font-serif-display text-3xl text-white mb-2">{m.t}</h3>
                  <p className="text-white/50 font-light max-w-xl leading-relaxed">{m.d}</p>
                </div>
                <Icon size={28} className="text-white/30 group-hover:text-rose transition-colors duration-500 hidden md:block" />
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-12" data-testid="bestsellers-section">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl tracking-tight">Mais Vendidos</h2>
          <Link to="/loja" className="label-caps text-white/60 hover:text-rose transition-colors duration-300 flex items-center gap-2">Ver tudo <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {bestsellers.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
